// src/components/Contact.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const Contact = () => {
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    category: 'general' // Default category
  });
  
  // Auto-populate name and email if user is logged in
  useEffect(() => {
    if (currentUser) {
      setFormData(prev => ({
        ...prev,
        name: currentUser.username || '',
        email: currentUser.email || ''
      }));
    }
  }, [currentUser]);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Send message to be stored in the database
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
        credentials: 'include',
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send message');
      }
      
      setSubmitStatus({
        type: 'success',
        message: 'Your message has been sent successfully. We will get back to you soon!'
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        message: '',
        category: 'general'
      });
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: error.message || 'There was an error sending your message. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 py-8 text-gray-100">
      <div className="max-w-2xl mx-auto px-4">
        <h1 className="text-3xl font-bold mb-6 text-blue-400">Contact Us</h1>
        
        <div className="bg-gray-800 rounded-lg shadow-md border border-gray-700 p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4 text-blue-300">Get in Touch</h2>
          <p className="text-gray-300 mb-6">
            We'd love to hear from you! Whether you have a question about our service, need help with something, or just want to say hello, feel free to reach out to us.
          </p>
          
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-2 text-blue-300">Email</h3>
            <p className="text-gray-300 mb-4">
              <a href="mailto:onlythebestboardgames@gmail.com" className="text-blue-400 hover:underline">
                onlythebestboardgames@gmail.com
              </a>
            </p>
          </div>
          
          <div className="border-t border-gray-700 pt-6">
            <h2 className="text-xl font-semibold mb-4 text-blue-300">Send a Message</h2>
            
            {submitStatus && (
              <div className={`mb-4 p-3 rounded-md ${
                submitStatus.type === 'success' 
                  ? 'bg-green-900/30 border border-green-700 text-green-200' 
                  : 'bg-red-900/30 border border-red-700 text-red-200'
              }`}>
                {submitStatus.message}
              </div>
            )}
            
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1">
                  Your Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full p-2 rounded-md border border-gray-700 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                  Your Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full p-2 rounded-md border border-gray-700 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="mb-4">
                <label htmlFor="category" className="block text-sm font-medium text-gray-300 mb-1">
                  Category
                </label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full p-2 rounded-md border border-gray-700 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="general">General Inquiry</option>
                  <option value="feedback">Feedback</option>
                  <option value="support">Support</option>
                  <option value="bug">Bug Report</option>
                </select>
              </div>
              
              <div className="mb-4">
                <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-1">
                  Your Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  className="w-full p-2 rounded-md border border-gray-700 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                ></textarea>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-2 px-4 rounded-md text-white font-medium ${
                  isSubmitting 
                    ? 'bg-blue-700 cursor-not-allowed' 
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;